import { ERROR_STATUS_CODES } from "@/constants/error";
import { EVENTS } from "@/constants/socket";
import { authTokenManager } from "@/services/auth-token-manager";
import { toast } from "@/utils/toast";
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

const SOCKET_SERVER_URL = process.env.REACT_APP_API_HOST || "http://localhost:3000";

interface ISocketContext {
  socket: Socket | null;
  connected: boolean;
}

interface SocketProviderProps {
  children: ReactNode;
}

// Create a context
const SocketContext = createContext<ISocketContext | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }

  return context;
};
export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const connectSocket = useCallback(() => {
    const token = authTokenManager.getToken();
    if (!token) return;

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socketInstance = io(SOCKET_SERVER_URL, {
      extraHeaders: { Authorization: `Bearer ${token}` },
    });

    socketRef.current = socketInstance;
    setSocket(socketInstance);
  }, []);

  useEffect(() => {
    const unsubscribe = authTokenManager.onTokenChange((token) => {
      if (token) {
        connectSocket();
      } else {
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        setSocket(null);
        setConnected(false);
      }
    });

    return unsubscribe;
  }, [connectSocket]);

  useEffect(() => {
    connectSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [connectSocket]);

  useEffect(() => {
    if (!socket) return;

    const handleError = (message: string) => {
      if (message === ERROR_STATUS_CODES.UNAUTHORIZED) {
        authTokenManager.refreshToken();
      } else {
        toast.error(message);
      }
    };

    const handleConnected = () => {
      setConnected(true);
    };

    const handleDisconnect = () => {
      setConnected(false);
    };

    socket.on(EVENTS.GLOBAL.ERROR, handleError);
    socket.on(EVENTS.GLOBAL.CONNECTED, handleConnected);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off(EVENTS.GLOBAL.ERROR, handleError);
      socket.off(EVENTS.GLOBAL.CONNECTED, handleConnected);
      socket.off("disconnect", handleDisconnect);
    };
  }, [socket]);

  return <SocketContext.Provider value={{ socket, connected }}>{children}</SocketContext.Provider>;
};
